import { Directive, Input, ElementRef, Renderer2, OnInit, HostListener, Optional } from '@angular/core';
import { PlessDialogService } from './pless-dialog-service';
import { CdkDrag } from '@angular/cdk/drag-drop';

/** ムーブだと判定する上からのサイズ(タイトル部分の高さ) */
const MOVE_OFFSET = 35;

/** リサイズだと判定する領域サイズ */
const RESIZE_OFFSET = 20;

/** 左上を示す値 */
const RESIZE_TYPE_NW = 'NW';
/** 上を示す値 */
const RESIZE_TYPE_N = 'N';
/** 右上を示す値 */
const RESIZE_TYPE_NE = 'NE';
/** 左を示す値 */
const RESIZE_TYPE_W = 'W';
/** 右を示す値 */
const RESIZE_TYPE_E = 'E';
/** 左下を示す値 */
const RESIZE_TYPE_SW = 'SW';
/** 下を示す値 */
const RESIZE_TYPE_S = 'S';
/** 右下を示す値 */
const RESIZE_TYPE_SE = 'SE';

/** 対象Elementに付与するCLASS名(zIndexの探索に使用) */
const CLASS_NAME = 'pless-title-window';

@Directive({
  selector: 'table[plessTitleWindow], div[plessTitleWindow]',
})

/**
 * ウインドウ表示機能を提供するディレクティブ
 */
export class PlessTitleWindowDirective implements OnInit {

  /** ドラッグが開始されたX座標 */
  protected dragStartX: number;
  /** ドラッグが開始されたY座標 */
  protected dragStartY: number;

  /** ドラッグが開始された時のドラッグ対象の幅 */
  protected dragStartWidth: number;
  /** ドラッグが開始された時のドラッグ対象の高さ */
  protected dragStartHeight: number;
  /** ドラッグが開始された時のドラッグ対象のTop位置 */
  protected dragStartTop: number;
  /** ドラッグが開始された時のドラッグ対象のLeft位置 */
  protected dragStartLeft: number;

  /** ドラッグが開始された時のリサイズの種類(どの方向にリサイズするか)
   *    NW  N  NE　←この部分はムーブになる為リサイズでは使用しない
   *    W       E
   *    SW  S  SE
   */
  protected dragStartResizeType: string;

  /** ムーブ中かどうかを示すフラグ */
  protected moveFlag = false;
  /** リサイズ中かどうかを示すフラグ */
  protected resizeFlag = false;

  /** 
   * ムーブ可能かどうかの指定
   * → @angular/cdkのドラッグ＆ドロップ機能を使用する
   * @deprecated
   */
  @Input() windowMovable = false;

  /** リサイズ可能かどうかの指定 */
  @Input() windowResizable = false;
  @Input() frontDispFlag = false;

  constructor(protected elementRef: ElementRef, protected renderer: Renderer2,
    @Optional() protected cdkDrag: CdkDrag) {
  }

  ngOnInit() {
    if (this.windowMovable || this.windowResizable) {
      // ムーブもしくはリサイズ可能な場合は強制的に"draggable"を"true"に設定
      this.setElementAttributeDraggable(true);
    }
    if (this.windowMovable || this.windowResizable || this.cdkDrag) {
      // Classを追加
      this.renderer.addClass(this.elementRef.nativeElement, CLASS_NAME);
    }

    // 最前面に追加する
    this.addToFront();
  }

  /* ============================================================
      ムーブ・リサイズに関する処理
  ============================================================ */

  /** マウス移動時の処理(リサイズのマウスカーソル表示処理) */
  @HostListener('mousemove', ['$event'])
  handleMousemove(event: MouseEvent) {
    // 操作対象の矩形情報を取得
    const rect = this.elementRef.nativeElement.getBoundingClientRect();
    // マウス座標を取得
    const offsetX = event.clientX - rect.left;
    const offsetY = event.clientY - rect.top;
    // サイズ情報を取得
    const height = rect.height;
    const width = rect.width;
    // Draggableを動的に設定(IE対策)
    this.setDraggable(offsetX, offsetY, height, width);
    // リサイズの種類を判定し、マウスカーソルを設定
    if (this.windowResizable) {
      const resizeType = this.getResizeType(offsetX, offsetY, height, width);
      this.setCursor(resizeType);
    }
  }

  /** マウスが離れた時の処理 */
  @HostListener('mouseout', ['$event'])
  handleMouseout(event: MouseEvent) {
    // マウスカーソルを戻す
    this.setCursor(null);
  }

  /** ドラッグが開始された時の処理 */
  @HostListener('dragstart', ['$event'])
  handleDragstart(event: DragEvent) {
    // 操作対象の矩形情報を取得
    const rect = this.elementRef.nativeElement.getBoundingClientRect();
    // ドラッグが開始されたドラッグ対象上のマウス座標を取得
    const offsetX = event.clientX - rect.left;
    const offsetY = event.clientY - rect.top;
    // ドラッグが開始されたマウス座標を取得して保持する
    this.dragStartX = event.clientX;
    this.dragStartY = event.clientY;
    // ドラッグ対象の情報を取得
    this.dragStartHeight = rect.height;
    this.dragStartWidth = rect.width;
    this.dragStartTop = rect.top;
    this.dragStartLeft = rect.left;
    // ムーブ判定
    this.moveFlag = this.isMovableArea(offsetY);
    // リサイズ判定
    this.dragStartResizeType = this.getResizeType(offsetX, offsetY, this.dragStartHeight, this.dragStartWidth);
    this.resizeFlag = this.isResizableArea(offsetX, offsetY, this.dragStartHeight, this.dragStartWidth);
    // イベント発生
    /*
    if (this.moveFlag) {
        // ムーブ開始イベントを発生させる
        this.dispatchEvent('movestart');
    } else if (this.resizeFlag) {
        // リサイズ開始イベントを発生させる
        this.dispatchEvent('resizestart');
    }
    */
  }

  /** ドラッグ中の処理 */
  @HostListener('drag', ['$event'])
  handleDrag(event: DragEvent) {
    // 現在のマウス座標を取得
    const x = event.clientX;
    const y = event.clientY;

    if (x === 0 && y === 0) {
      return; // ドラッグ終了時、座標が(0,0)になる事への対策
    }

    // マウスの移動距離を取得
    const moveX = x - this.dragStartX;
    const moveY = y - this.dragStartY;

    if (this.moveFlag) {
      // ムーブ処理中の場合、Top位置・Left位置をマウス移動分だけ変更
      const top = this.dragStartTop + moveY;
      const left = this.dragStartLeft + moveX;
      this.renderer.setStyle(this.elementRef.nativeElement, 'top', top + 'px');
      this.renderer.setStyle(this.elementRef.nativeElement, 'left', left + 'px');
      // ムーブ中イベントを発生させる
      // this.dispatchEvent('move');
    } else if (this.resizeFlag) {
      // リサイズ処理中の場合、幅・高さをマウス移動分だけ変更
      if (this.dragStartResizeType.indexOf(RESIZE_TYPE_S) !== -1) {           // 下
        // const top = this.dragStartTop;
        const height = this.dragStartHeight + moveY;
        // this.renderer.setStyle(this.elementRef.nativeElement, 'top', top + 'px');
        // this.renderer.setStyle(this.elementRef.nativeElement, 'height', height + 'px');
        this.renderer.setStyle(this.elementRef.nativeElement, 'height', height + 'px');
      }
      if (this.dragStartResizeType.indexOf(RESIZE_TYPE_W) !== -1) {           // 左
        const left = this.dragStartLeft + moveX;
        const width = this.dragStartWidth - moveX;
        this.renderer.setStyle(this.elementRef.nativeElement, 'left', left + 'px');
        this.renderer.setStyle(this.elementRef.nativeElement, 'width', width + 'px');
        // サイズ変更後に位置がずれる事への対策
        const rect = this.elementRef.nativeElement.getBoundingClientRect();
        const differenceL = left - rect.left;
        if (differenceL !== 0) {
          this.renderer.setStyle(this.elementRef.nativeElement, 'left', (left + differenceL) + 'px');
        }
      } else if (this.dragStartResizeType.indexOf(RESIZE_TYPE_E) !== -1) {    // 右
        // const left = this.dragStartLeft;
        const width = this.dragStartWidth + moveX;
        // this.renderer.setStyle(this.elementRef.nativeElement, 'left', left + 'px');
        this.renderer.setStyle(this.elementRef.nativeElement, 'width', width + 'px');
      }
      // リサイズ中イベントを発生させる
      // this.dispatchEvent('resize');
    }
  }

  /** ドラッグが終了した時の処理 */
  @HostListener('dragend', ['$event'])
  handleDragend(event: DragEvent) {
    // イベント発生
    /*
    if (this.moveFlag) {
        // ムーブ終了イベントを発生させる
        this.dispatchEvent('moveend');
    } else if (this.resizeFlag) {
        // リサイズ終了イベントを発生させる
        this.dispatchEvent('resizeend');
    }
    */
    // 各種値のクリア
    this.moveFlag = false;
    this.resizeFlag = false;
    this.dragStartResizeType = null;
    this.setCursor(this.dragStartResizeType);
    this.setElementAttributeDraggable(false);
  }

  /**
   * マウスの位置からムーブ可能領域かどうかを取得する
   */
  protected isMovableArea(offsetY: number): boolean {
    if (this.windowMovable) {
      if (offsetY < MOVE_OFFSET) {
        // 上から指定pxの範囲でドラッグされた場合はムーブ領域と判定
        return true;
      }
    }
    return false;
  }

  /**
   * マウスの位置からリサイズ可能領域かどうかを取得する
   */
  protected isResizableArea(offsetX: number, offsetY: number, height: number, width: number): boolean {
    if (this.windowResizable) {
      const resizeType = this.getResizeType(offsetX, offsetY, height, width);
      if (resizeType != null && resizeType !== RESIZE_TYPE_N) {   // 上部のサイズ変更は移動と被るので対象外にしておく
        return true;
      }
    }
    return false;
  }

  /**
   * マウスの位置からリサイズの種類を判定
   */
  protected getResizeType(offsetX: number, offsetY: number, height: number, width: number): string {
    let resizeType: string;

    if (offsetY < RESIZE_OFFSET) {                      // 上(N)かどうかの判定
      resizeType = RESIZE_TYPE_N;
    } else if (offsetY > (height - RESIZE_OFFSET)) {    // 下(S)かどうかの判定
      resizeType = RESIZE_TYPE_S;
    }

    if (offsetX < RESIZE_OFFSET) {                      // 左(W)かどうかの判定
      if (resizeType == null) {
        resizeType = '';
      }
      resizeType += RESIZE_TYPE_W;
    } else if (offsetX > (width - RESIZE_OFFSET)) {     // 右(E)かどうかの判定
      if (resizeType == null) {
        resizeType = '';
      }
      resizeType += RESIZE_TYPE_E;
    }

    return resizeType;
  }

  /**
   * リサイズの種類に応じたマウスカーソルを設定
   */
  protected setCursor(resizeType: string) {
    let cursor: string;
    switch (resizeType) {
      /* 上部のサイズ変更は移動と被るので対象外にしておく
                  case RESIZE_TYPE_NW:
                      cursor = 'nw-resize';
                      break;
                  case RESIZE_TYPE_N:
                      cursor = 'n-resize';
                      break;
                      case RESIZE_TYPE_NE:
                      cursor = 'ne-resize';
                      break;
      */
      case RESIZE_TYPE_W:
        cursor = 'w-resize';
        break;
      case RESIZE_TYPE_E:
        cursor = 'e-resize';
        break;
      case RESIZE_TYPE_SW:
        cursor = 'sw-resize';
        break;
      case RESIZE_TYPE_S:
        cursor = 's-resize';
        break;
      case RESIZE_TYPE_SE:
        cursor = 'se-resize';
        break;
      default:
        cursor = 'auto';
        break;
    }
    this.renderer.setStyle(this.elementRef.nativeElement, 'cursor', cursor);
  }

  /**
   * ドラッグ対象のDraggable指定
   */
  protected setElementAttributeDraggable(draggable: boolean) {
    if (draggable) {
      this.renderer.setAttribute(this.elementRef.nativeElement, 'draggable', 'true');
    } else {
      this.renderer.setAttribute(this.elementRef.nativeElement, 'draggable', null);
    }
  }

  /**
   * ドラッグ対象のDraggableを動的に制御
   * ※IEにおいてDraggableをtrueにした場合、ダブルクリックしないとテキスト入力にフォーカスが当たらない事への対策
   */
  protected setDraggable(offsetX: number, offsetY: number, height: number, width: number) {
    // ドラッグ操作中は常にtrue
    if (this.moveFlag || this.resizeFlag) {
      this.setElementAttributeDraggable(true);
      return;
    }
    // マウス位置がムーブもしくはリサイズ可能領域の場合のみtrue
    const movable = this.isMovableArea(offsetY);
    const resizable = this.isResizableArea(offsetX, offsetY, height, width);
    this.setElementAttributeDraggable((movable || resizable));
  }


  /* ============================================================
      ウインドウの表示(前面・背面)に関する処理
  ============================================================ */

  /** クリック時の処理 */
  @HostListener('mousedown', ['$event'])
  handleClick(event: MouseEvent) {
    // Windowを最前面に移動
    this.bringToFront();
  }

  /**
   * 最前面に追加する
   */
  protected addToFront() {
    const maxZIndex = this.getMaxZIndex();
    const zIndex = maxZIndex + 1;
    this.renderer.setStyle(this.elementRef.nativeElement, 'zIndex', zIndex.toString());
  }

  /**
   * 最前面に表示する
   */
  protected bringToFront() {
    const zIndex: number = this.elementRef.nativeElement.style.zIndex;
    const maxZIndex = this.getMaxZIndex();
    // 自身より前面に表示されているものを背面に回す
    this.sendToBack(zIndex, maxZIndex);
    // 自身を最前面に表示する
    this.renderer.setStyle(this.elementRef.nativeElement, 'zIndex', maxZIndex.toString());
    // this.dispatchEvent('bringtofront');
  }

  /**
   * 自身より前に表示されているものは背面に送る(zIndexを-1する)
   */
  sendToBack(targetZIndex: number, maxZIndex: number) {
    const defaultView = document.defaultView;
    const elements = document.getElementsByClassName(CLASS_NAME);
    const length = elements.length;
    for (let i = 0; i < length; i++) {
      const element = elements[i];
      let zIndex = element['style'].zIndex;
      if (!zIndex) {
        const css = element['currentStyle'] || defaultView.getComputedStyle(element, null);
        zIndex = css ? css.zIndex : 0;
      }
      zIndex -= 0;
      if (zIndex > targetZIndex) {
        zIndex--;
        element['style'].zIndex = zIndex;
      }
    }
  }

  /**
   * 表示しているplessTitleWindowのzIndex最大値を取得する
   */
  getMaxZIndex(): number {
    let maxZIndex = PlessDialogService.MIN_Z_INDEX;
    const defaultView = document.defaultView;
    const elements = document.getElementsByClassName(CLASS_NAME);
    const length = elements.length;
    for (let i = 0; i < length; i++) {
      const element = elements[i];
      let zIndex = element['style'].zIndex;
      if (!zIndex) {
        const css = element['currentStyle'] || defaultView.getComputedStyle(element, null);
        zIndex = css ? css.zIndex : 0;
      }
      zIndex -= 0;
      if (maxZIndex < zIndex) {
        maxZIndex = zIndex;
      }
    }
    return maxZIndex;
  }

  /* ============================================================
      独自イベントに関する処理
  ============================================================ */
  /*
  private dispatchEvent(typArg: string) {
      this.elementRef.nativeElement.dispatchEvent(new CustomEvent(typArg, {
          // detailにRectの内容をセットする
          detail: {
              rect: this.elementRef.nativeElement.getBoundingClientRect(),
          }
      }));
  }
  */
}
